import * as _ from 'lodash';
import * as React from 'react';
import * as Validator from 'validator';
import { withStyles, Button, Grid, IconButton, FormControl, FormGroup, FormHelperText, Input, InputLabel, Snackbar, WithStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { nameofFactory } from '../../utils/nameofFactory';

//#region UI configuration
const strings = {
    loginButton: 'Log in',
    userNameLabel: 'Username',
    userNamePlaceholder: 'Enter username',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter password',
    userNameIsRequired: 'Username is required',
    passwordIsRequired: 'Password is required',
    loginFailed: 'Login failed'
};

const styles = () => ({
    container: {
        width: 310
    } as CSSProperties,
    loginButtonContainer: {
        marginTop: 16
    } as CSSProperties,
    loginButton: {
        fontWeight: 400,
        letterSpacing: 1
    } as CSSProperties
});

type Styles = WithStyles<'container' | 'loginButtonContainer' | 'loginButton'>;
//#endregion

//#region Form configuration
const enum LoginFormFieldNames {
    USER_NAME = 'userName',
    PASSWORD = 'password'
}

interface LoginFormFields {
    userName: string;
    password: string;
}

interface LoginFormErrors {
    userName?: string;
    password?: string;
    form?: string;
}
//#endregion

//#region Props & State
interface LoginFormProps {
    readonly submit: (userName: string, password: string) => Promise<void>;
}
interface LoginFormState {
    fields: LoginFormFields;
    errors: LoginFormErrors;
    loading: boolean;
}

type PropsWithStyles = LoginFormProps & Styles;

const initialState: LoginFormState = {
    fields: {
        userName: '',
        password: ''
    },
    errors: {},
    loading: false
};
//#endregion

class LoginForm extends React.Component<PropsWithStyles, LoginFormState> {

    private usernameInput: HTMLInputElement;
    private passwordInput: HTMLInputElement;

    constructor(props: PropsWithStyles) {
        super(props);

        this.state = initialState;
    }

    render() {
        const { fields, errors, loading } = this.state;
        const { classes } = this.props;

        return (
            <Grid container={true} justify="center" spacing={0}>
                <Grid item={true} className={classes.container}>
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        open={!!errors.form}
                        autoHideDuration={6000}
                        onClose={this.onFormErrorClose}
                        message={<span>{errors.form}</span>}
                        action={[
                            <IconButton
                                key="close"
                                color="inherit"
                                onClick={this.onFormErrorClose}
                            >
                                <Close />
                            </IconButton>
                        ]}
                    />

                    <form onSubmit={this.onFormSubmit}>
                        <FormGroup>
                            <FormControl error={!!errors.userName} margin="none">
                                <InputLabel>{strings.userNameLabel}</InputLabel>
                                <Input
                                    id={LoginFormFieldNames.USER_NAME}
                                    inputRef={ref => this.usernameInput = ref}
                                    name={LoginFormFieldNames.USER_NAME}
                                    onChange={this.onFieldChange}
                                    placeholder={strings.userNamePlaceholder}
                                    type="text"
                                    value={fields.userName}
                                />
                                <FormHelperText>{errors.userName}</FormHelperText>
                            </FormControl>

                            <FormControl error={!!errors.password} margin="normal">
                                <InputLabel>{strings.passwordLabel}</InputLabel>
                                <Input
                                    id={LoginFormFieldNames.PASSWORD}
                                    inputRef={ref => this.passwordInput = ref}
                                    name={LoginFormFieldNames.PASSWORD}
                                    onChange={this.onFieldChange}
                                    placeholder={strings.passwordPlaceholder}
                                    type="password"
                                    value={fields.password}
                                />
                                <FormHelperText>{errors.password}</FormHelperText>
                            </FormControl>
                        </FormGroup>

                        <Grid container={true} spacing={0} className={classes.loginButtonContainer} justify="flex-end">
                            <Button
                                classes={{ label: classes.loginButton }}
                                color="primary"
                                disabled={loading}
                                type="submit"
                                variant="raised"
                            >
                                {strings.loginButton}
                            </Button>
                        </Grid>
                    </form>
                </Grid>
            </Grid>
        );
    }

    //#region Private
    private validateFormFields(fields: LoginFormFields): LoginFormErrors {
        const errors: LoginFormErrors = {};

        if (Validator.isEmpty(fields.userName)) {
            errors.userName = strings.userNameIsRequired;
        }

        if (Validator.isEmpty(fields.password)) {
            errors.password = strings.passwordIsRequired;
        }

        return errors;
    }

    private onFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            fields: { ...this.state.fields, [event.target.name]: event.target.value }
        });
    }

    private onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const errors: LoginFormErrors = this.validateFormFields(this.state.fields);
        this.setState({ errors });

        if (_.isEmpty(errors)) {
            const { userName, password } = this.state.fields;
            this.setState({ loading: true });
            this.props.submit(userName, password)
                .catch(() => {
                    this.setState({
                        loading: false,
                        errors: { form: strings.loginFailed }
                    });
                });
        } else if (!!errors.userName) {
            this.usernameInput.focus();
        } else if (!!errors.password) {
            this.passwordInput.focus();
        }
    }

    private onFormErrorClose = () => {
        const nameof = nameofFactory<LoginFormErrors>();
        const errors: LoginFormErrors = _.omit(this.state.errors, nameof('form'));
        this.setState({ errors });
    }
    //#endregion
}

export default withStyles(styles)<LoginFormProps>(LoginForm);



// WEBPACK FOOTER //
// ./src/modules/login/LoginForm.tsx